import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GenericModal } from "@/components/GenericModal/GenericModal";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { OrphanBillCandidate, searchOrphanBills } from "@/api/bills";
import { reconcileOrphanSale } from "@/api/flashscan";
import { resolveSaleManually } from "@/api/sales";

import "./FlashscanOrphanSaleModal.scss";

export type OrphanSaleTarget = {
  id: string;
  providerId: string;
  name?: string;
  accountName: string;
  amount: number;
  closeDate?: string;
};

export const FlashscanOrphanSaleModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  target: OrphanSaleTarget | null;
  onResolved: () => Promise<void> | void;
}> = ({ isOpen, onClose, target, onResolved }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<OrphanBillCandidate[]>([]);
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [ignoreComment, setIgnoreComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saleDateLabel = useMemo(() => {
    if (!target?.closeDate) return "-";
    const d = new Date(target.closeDate);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("fr-FR");
  }, [target?.closeDate]);

  const close = useCallback(() => {
    setQ("");
    setResults([]);
    setSelectedBillId(null);
    setIgnoreComment("");
    setIsLoading(false);
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      setIsLoading(true);
      try {
        const res = await searchOrphanBills("");
        setResults(res.data);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = window.setTimeout(() => {
      void (async () => {
        const query = q.trim();
        // Ne lancer la recherche qu'à partir de 3 caractères (UX + perf)
        if (query.length > 0 && query.length < 3) return;

        setIsLoading(true);
        try {
          const res = await searchOrphanBills(query);
          setResults(res.data);
        } finally {
          setIsLoading(false);
        }
      })();
    }, 250);

    return () => window.clearTimeout(handle);
  }, [isOpen, q]);

  const onIgnore = useCallback(async () => {
    if (!target) return;
    setIsSubmitting(true);
    try {
      await resolveSaleManually(target.id, {
        action: "IGNORE",
        userComment: ignoreComment.trim() || null,
      });
      await onResolved();
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, ignoreComment, onResolved, target]);

  const onReconcile = useCallback(async () => {
    if (!target || !selectedBillId) return;
    setIsSubmitting(true);
    try {
      await reconcileOrphanSale(target.id, selectedBillId);
      await onResolved();
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, onResolved, selectedBillId, target]);

  return (
    <GenericModal
      isOpen={isOpen}
      title={target?.accountName || t("pages.flashscan.orphanSale.titleFallback")}
      onClose={close}
      className="flashscan-orphan-sale-modal"
    >
      {target ? (
        <div className="flashscan-orphan-sale-modal-content">
          <div className="flashscan-orphan-sale-modal-section-title">
            {t("pages.flashscan.orphanSale.orphanDataTitle")}
          </div>
          <div className="flashscan-orphan-sale-modal-orphan-card">
            <div className="flashscan-orphan-sale-modal-orphan-grid">
              <div>
                <div className="flashscan-orphan-sale-modal-label">
                  {t("pages.flashscan.orphanSale.fields.client")}
                </div>
                <div className="flashscan-orphan-sale-modal-value">
                  {target.accountName}
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-sale-modal-label">
                  {t("pages.flashscan.orphanSale.fields.date")}
                </div>
                <div className="flashscan-orphan-sale-modal-value">
                  {saleDateLabel}
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-sale-modal-label">
                  {t("pages.flashscan.orphanSale.fields.amount")}
                </div>
                <div className="flashscan-orphan-sale-modal-value">
                  {target.amount.toLocaleString("fr-FR")} €
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-sale-modal-label">
                  {t("pages.flashscan.orphanSale.fields.reference")}
                </div>
                <div className="flashscan-orphan-sale-modal-value">
                  {target.providerId}
                </div>
              </div>
            </div>
          </div>

          <div className="flashscan-orphan-sale-modal-section-title">
            {t("pages.flashscan.orphanSale.findMatchTitle")}
          </div>

          <div className="flashscan-orphan-sale-modal-search-row">
            <input
              className="flashscan-orphan-sale-modal-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("pages.flashscan.orphanSale.searchPlaceholder")}
            />
          </div>

          <div className="flashscan-orphan-sale-modal-results">
            {isLoading ? (
              <div className="flashscan-orphan-sale-modal-muted">
                {t("pages.flashscan.orphanSale.loading")}
              </div>
            ) : results.length === 0 ? (
              <div className="flashscan-orphan-sale-modal-muted">
                {t("pages.flashscan.orphanSale.noResults")}
              </div>
            ) : (
              results.map((bill) => {
                const isSelected = bill.id === selectedBillId;
                const start = new Date(bill.startPeriod);
                const end = new Date(bill.endPeriod);
                const period =
                  Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
                    ? "-"
                    : `${start.toLocaleDateString("fr-FR")} → ${end.toLocaleDateString(
                        "fr-FR",
                      )}`;

                return (
                  <button
                    key={bill.id}
                    type="button"
                    className={`flashscan-orphan-sale-modal-result ${
                      isSelected ? "is-selected" : ""
                    }`}
                    onClick={() =>
                      setSelectedBillId((prev) =>
                        prev === bill.id ? null : bill.id,
                      )
                    }
                  >
                    <div className="flashscan-orphan-sale-modal-result-main">
                      <div className="flashscan-orphan-sale-modal-result-title">
                        {bill.accountName ?? "-"}
                      </div>
                      <div className="flashscan-orphan-sale-modal-result-subtitle">
                        {bill.providerId}
                      </div>
                      <div className="flashscan-orphan-sale-modal-result-subtitle">
                        {bill.soldItemsDescription ?? "-"}
                      </div>
                      <div className="flashscan-orphan-sale-modal-result-subtitle">
                        {period}
                      </div>
                    </div>
                    <div className="flashscan-orphan-sale-modal-result-amount">
                      {bill.amountDue.toLocaleString("fr-FR")} €
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="flashscan-orphan-sale-modal-comment">
            <div className="flashscan-orphan-sale-modal-comment-label">
              {t("pages.flashscan.orphanSale.commentLabel")}
            </div>
            <textarea
              className="flashscan-orphan-sale-modal-comment-input"
              rows={3}
              value={ignoreComment}
              onChange={(e) => setIgnoreComment(e.target.value)}
              placeholder={t("pages.flashscan.orphanSale.commentPlaceholder")}
            />
          </div>

          <div className="flashscan-orphan-sale-modal-footer">
            <button
              type="button"
              className="flashscan-orphan-sale-modal-ignore"
              onClick={() => void onIgnore()}
              disabled={isSubmitting}
            >
              {t("pages.flashscan.orphanSale.actions.ignore")}
            </button>
            <GenericButton
              label={t("pages.flashscan.orphanSale.actions.reconcile")}
              onClick={() => void onReconcile()}
              disabled={!selectedBillId || isSubmitting}
              theme="primary"
            />
          </div>
        </div>
      ) : null}
    </GenericModal>
  );
};

